import React from 'react'
import CustomTimePicker from '../TimePicker/CustomTimePicker'
import { Button } from '@/components/ui/button'
import { CField } from '@/_components/CField'
import CPhoneInput from '@/_components/PhoneInput'
import UniversalHeader from '@/_components/Header'
import {
  Box,
  Flex,
  HStack,
  Input,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react'
import { useTranslations } from 'next-intl'
import dayjs, { Dayjs } from 'dayjs'
import { useRouter } from '@/i18n/navigation'
import BranchSelect from '../branch-select'
import FormSelect from './FormSelect'
import { ReservationFormViewProps } from './types'

export default function ReservationFormMobile({
  onSubmit,
  formData,
  setFormData,
  workHours,
  isEditable,
  isSaving,
  reservationId,
  externalId,
  autoOpenPicker,
  setAutoOpenPicker,
  sectionOptions,
  tableOptions,
  canFetchAvailability,
  isSectionsLoading,
  isTablesLoading,
}: ReservationFormViewProps) {
  const t = useTranslations()
  const router = useRouter()

  const pageTitle =
    reservationId && !isEditable
      ? `${t('reservation_details')} #${externalId}`
      : t('book_table')

  return (
    <Box bg='background' minH='100dvh'>
      <UniversalHeader
        onBack={() => router.push('/orders?order_type=hall')}
        backLink='/orders?order_type=hall'
        title={pageTitle}
      />
      <Box p={4}>
        <form onSubmit={onSubmit}>
          <Box
            bg='background.card'
            rounded='12px'
            p={4}
            boxShadow='0px 1px 2px 0px #1018280D'
          >
            <Stack gap={4}>
              <CField label={t('name')}>
                <Input
                  className='peer'
                  value={formData.customer_name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      customer_name: e.target.value,
                    }))
                  }
                  readOnly={!isEditable}
                  disabled={!isEditable}
                />
              </CField>

              <Stack gap={4}>
                <CPhoneInput
                  label={t('phone_number')}
                  value={formData.phone_number}
                  onChange={() => {}}
                  disabled
                />
                <CPhoneInput
                  label={t('extra_phone_number')}
                  value={formData.extra_phone_number}
                  onChange={(e) =>
                    typeof e === 'string' &&
                    setFormData((prev) => ({
                      ...prev,
                      extra_phone_number: e,
                    }))
                  }
                  disabled={!isEditable}
                />
              </Stack>

              <CField label={t('write_comment')}>
                <Textarea
                  className='peer'
                  minH='204px'
                  resize='none'
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  readOnly={!isEditable}
                  disabled={!isEditable}
                  bg='background'
                  border='none'
                  rounded='2xl'
                  px={4}
                  py={7}
                />
              </CField>

              <BranchSelect
                formData={formData}
                setFormData={setFormData}
                isEditable={isEditable}
              />

              <CField label={t('select_date')}>
                <Input
                  type='date'
                  className='peer'
                  min={dayjs().format('YYYY-MM-DD')}
                  value={
                    formData.reserve_date
                      ? dayjs(formData.reserve_date).format('YYYY-MM-DD')
                      : ''
                  }
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      reserve_date: dayjs(e.target.value),
                    }))
                    setAutoOpenPicker('start')
                  }}
                  readOnly={!isEditable}
                  disabled={!isEditable}
                />
              </CField>

              <HStack align='flex-start'>
                <CField label={t('from_time')} isLongLabel={false}>
                  <CustomTimePicker
                    placeholder={t('from_time')}
                    max={workHours?.to_time || null}
                    min={workHours?.from_time || null}
                    value={formData.start_time ?? null}
                    onChange={(newValue: Dayjs | null) => {
                      setFormData((prev) => ({
                        ...prev,
                        start_time: newValue,
                      }))
                      setAutoOpenPicker('end')
                    }}
                    disabled={
                      !isEditable ||
                      dayjs(formData.reserve_date).isBefore(dayjs(), 'day')
                    }
                    isTodaySelected={
                      formData.reserve_date
                        ? dayjs(formData.reserve_date).isSame(dayjs(), 'day')
                        : false
                    }
                    open={autoOpenPicker === 'start'}
                    onClose={() => setAutoOpenPicker('end')}
                  />
                </CField>
                <CField label={t('reserve_table_to_time')} isLongLabel={false}>
                  <CustomTimePicker
                    placeholder={t('reserve_table_to_time')}
                    max={workHours?.to_time || null}
                    min={workHours?.from_time || null}
                    value={formData.end_time ?? null}
                    onChange={(newValue: Dayjs | null) => {
                      setFormData((prev) => ({
                        ...prev,
                        end_time: newValue,
                      }))
                      setAutoOpenPicker('')
                    }}
                    disabled={
                      !isEditable ||
                      dayjs(formData.reserve_date).isBefore(dayjs(), 'day')
                    }
                    isTodaySelected={
                      formData.reserve_date
                        ? dayjs(formData.reserve_date).isSame(dayjs(), 'day')
                        : false
                    }
                    open={autoOpenPicker === 'end'}
                    onClose={() => setAutoOpenPicker('')}
                    fromTime={formData.start_time}
                  />
                </CField>
              </HStack>

              <FormSelect
                label={t('select_section')}
                value={formData.section_id}
                options={sectionOptions}
                onChange={(option) =>
                  setFormData((prev) => ({
                    ...prev,
                    section_id: option,
                    table_id: null,
                  }))
                }
                isDisabled={!isEditable || !canFetchAvailability}
                isLoading={isSectionsLoading}
              />

              <FormSelect
                label={t('select_table')}
                value={formData.table_id}
                options={tableOptions}
                onChange={(option) =>
                  setFormData((prev) => ({
                    ...prev,
                    table_id: option,
                  }))
                }
                isDisabled={
                  !isEditable ||
                  !canFetchAvailability ||
                  !formData.section_id?.value
                }
                isLoading={isTablesLoading}
              />
            </Stack>

            {isEditable && (
              <Flex mt={4}>
                <Button
                  w='full'
                  h='52px'
                  bg='primary'
                  color='primary.text'
                  _hover={{ bg: 'primary.hover' }}
                  fontSize='xl'
                  type='submit'
                  disabled={isSaving}
                  loading={isSaving}
                >
                  {reservationId && reservationId !== 'create'
                    ? t('update')
                    : t('reserve')}
                </Button>
              </Flex>
            )}
          </Box>
        </form>
      </Box>
    </Box>
  )
}
